// V2 Decks library — folder sidebar + deck grid, with create-folder and
// move-deck-to-folder. Reuses home.css (.library/.lib-side/.lib-grid/.side-item/
// .deck-card). Light DOM. Responsive: the sidebar collapses above the grid on
// narrow viewports (library-page.css media query).

import { LitElement, html, nothing, type TemplateResult } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { api, HttpError } from '../api/client';
import { navigate } from '../lib/router-state';
import { mountThumbs } from '../lib/thumb';
import { isEmptyDraft } from '../lib/deck-util';
import { setPageTitle } from '../lib/page-title';
import type { Deck, Folder } from '../api/types';
import type { SlideDeck, Slide } from '../lib/slide-model';

const ALL = '__all__';
const NONE = '__none__';

@customElement('library-page')
export class LibraryPage extends LitElement {
  @state() private decks: Deck[] = [];
  @state() private folders: Folder[] = [];
  @state() private active = ALL;
  @state() private search = '';
  @state() private loading = true;
  @state() private error = '';
  @state() private menuFor = '';
  @state() private confirmDelete = '';
  @state() private addingFolder = false;
  @state() private folderName = '';

  protected createRenderRoot(): HTMLElement {
    return this;
  }

  connectedCallback(): void {
    super.connectedCallback();
    setPageTitle('Decks', 'Browse and organise your Folio decks.');
    void this.load();
  }

  private async load(): Promise<void> {
    this.loading = true;
    try {
      const [decks, folders] = await Promise.all([api.listDecks(), api.listFolders().catch(() => [])]);
      this.decks = decks;
      this.folders = folders;
    } catch (e) {
      this.error = e instanceof HttpError ? `Could not load decks (${e.status}).` : 'Could not load decks.';
    } finally {
      this.loading = false;
    }
  }

  private get filtered(): Deck[] {
    const q = this.search.trim().toLowerCase();
    return this.decks.filter((d) => {
      if (isEmptyDraft(d)) return false; // hide not-yet-generated empty drafts
      if (this.active === NONE && d.folderId) return false;
      if (this.active !== ALL && this.active !== NONE && d.folderId !== this.active) return false;
      if (q && !d.title.toLowerCase().includes(q)) return false;
      return true;
    });
  }

  /** The cover slide of a deck (for its card thumbnail), if it has a V2 model. */
  private coverOf(id: string): Slide | undefined {
    const m = this.decks.find((d) => d.id === id)?.model as unknown as SlideDeck | undefined;
    return m && m.schemaVersion === 2 ? m.slides[0] : undefined;
  }

  protected updated(): void {
    mountThumbs(this, (id) => this.coverOf(id));
  }

  private activeLabel(): string {
    if (this.active === ALL) return 'All decks';
    if (this.active === NONE) return 'Unfiled';
    return this.folders.find((f) => f.id === this.active)?.name || 'Decks';
  }

  private count(folderId: string): number {
    const real = this.decks.filter((d) => !isEmptyDraft(d)); // match the grid
    if (folderId === ALL) return real.length;
    if (folderId === NONE) return real.filter((d) => !d.folderId).length;
    return real.filter((d) => d.folderId === folderId).length;
  }

  private async createFolderNamed(name: string): Promise<void> {
    const n = name.trim();
    this.addingFolder = false; this.folderName = '';
    if (!n) return;
    try {
      const f = await api.createFolder(n, 'folder');
      this.folders = [...this.folders, f].sort((a, b) => a.name.localeCompare(b.name));
    } catch { this.error = 'Could not create folder.'; }
  }

  private async move(deck: Deck, folderId: string): Promise<void> {
    this.menuFor = '';
    try {
      await api.moveDeck(deck.id, folderId);
      this.decks = this.decks.map((d) => (d.id === deck.id ? { ...d, folderId: folderId || undefined } : d));
    } catch { this.error = 'Could not move deck.'; }
  }

  /** Delete a deck (owner-only). Optimistic: removed from the grid immediately,
   *  restored on failure. The menu's two-step confirm gates this so it is never
   *  a single misclick. */
  private async deleteDeck(deck: Deck): Promise<void> {
    this.menuFor = '';
    this.confirmDelete = '';
    const prev = this.decks;
    this.decks = this.decks.filter((d) => d.id !== deck.id);
    try {
      await api.deleteDeck(deck.id);
    } catch {
      this.decks = prev;
      this.error = 'Could not delete deck.';
    }
  }

  render(): TemplateResult {
    return html`
      <div class="library">
        <aside class="lib-side">
          <button class="btn btn--ai btn--block" style="margin-bottom:var(--s-4)" @click=${() => navigate('/studio')}>＋ New deck</button>
          <div class="side-group">
            <button class="side-item ${this.active === ALL ? 'is-active' : ''}" @click=${() => { this.active = ALL; }}>
              <span class="side-ico">▦</span><span class="side-name">All decks</span><span class="side-count">${this.count(ALL)}</span></button>
            <button class="side-item ${this.active === NONE ? 'is-active' : ''}" @click=${() => { this.active = NONE; }}>
              <span class="side-ico">◻</span><span class="side-name">Unfiled</span><span class="side-count">${this.count(NONE)}</span></button>
          </div>
          <div class="side-label">Folders</div>
          <div class="side-group">
            ${this.folders.map((f) => html`
              <button class="side-item ${this.active === f.id ? 'is-active' : ''}" @click=${() => { this.active = f.id; }}>
                <span class="side-ico">📁</span><span class="side-name">${f.name}</span><span class="side-count">${this.count(f.id)}</span></button>`)}
            ${this.addingFolder
              ? html`<input class="side-folder-input" placeholder="Folder name" autofocus
                  .value=${this.folderName}
                  @input=${(e: Event) => { this.folderName = (e.target as HTMLInputElement).value; }}
                  @keydown=${(e: KeyboardEvent) => { if (e.key === 'Enter') this.createFolderNamed(this.folderName); if (e.key === 'Escape') { this.addingFolder = false; this.folderName = ''; } }}
                  @blur=${() => this.createFolderNamed(this.folderName)}>`
              : html`<button class="side-item side-new" @click=${() => { this.addingFolder = true; }}>
                  <span class="side-ico">＋</span><span class="side-name">New folder</span></button>`}
          </div>
        </aside>

        <main class="lib-main">
          <div class="lib-bar">
            <div class="lib-bar-l">
              <h1 class="lib-h1">${this.activeLabel()}</h1>
              <span class="lib-count faint">${this.filtered.length} deck${this.filtered.length === 1 ? '' : 's'}</span>
            </div>
            <div class="lib-bar-r">
              <div class="search"><input type="search" placeholder="Search decks…"
                .value=${this.search} @input=${(e: Event) => { this.search = (e.target as HTMLInputElement).value; }}></div>
            </div>
          </div>
          ${this.error ? html`<div class="ws-error">${this.error}</div>` : nothing}
          ${this.loading
            ? html`<div class="lib-empty">Loading…</div>`
            : this.filtered.length === 0
              ? html`<div class="lib-empty"><div class="lib-empty-ico">▦</div><p>No decks here yet.</p>
                  <button class="btn btn--ai btn--sm" @click=${() => navigate('/studio')}>Start one</button></div>`
              : html`<div class="lib-grid deck-grid">
                  ${this.filtered.map((d) => this.renderCard(d))}
                </div>`}
        </main>
      </div>`;
  }

  private renderCard(d: Deck): TemplateResult {
    return html`
      <div class="deck-card">
        <div class="deck-thumb" data-thumb=${d.id} @click=${() => navigate(`/studio?deck=${encodeURIComponent(d.id)}`)}></div>
        <div class="deck-meta">
          <div class="deck-meta-top">
            <h3 class="deck-title" @click=${() => navigate(`/studio?deck=${encodeURIComponent(d.id)}`)}>${d.title}</h3>
            <button class="icon-btn deck-more" aria-label="More" @click=${() => { const open = this.menuFor === d.id; this.menuFor = open ? '' : d.id; this.confirmDelete = ''; }}>⋯</button>
          </div>
          <div class="deck-sub"><span class="faint">${d.status === 'draft' ? 'Draft' : 'Saved'}</span></div>
          ${this.menuFor === d.id
            ? html`<div class="menu" @click=${(e: Event) => e.stopPropagation()}>
                ${this.confirmDelete === d.id
                  ? html`
                    <div class="menu-confirm">
                      <p class="menu-confirm-q">Delete “${d.title}”?</p>
                      <p class="menu-confirm-sub faint">This can’t be undone.</p>
                      <div class="menu-confirm-row">
                        <button class="btn btn--sm btn--ghost" @click=${() => { this.confirmDelete = ''; }}>Cancel</button>
                        <button class="btn btn--sm btn--danger" @click=${() => this.deleteDeck(d)}>Delete</button>
                      </div>
                    </div>`
                  : html`
                    <div class="side-group">Move to…</div>
                    ${this.folders.map((f) => html`
                      <button class="menu-item ${d.folderId === f.id ? 'is-current' : ''}" @click=${() => this.move(d, f.id)}>📁 ${f.name}</button>`)}
                    <button class="menu-item" @click=${() => this.move(d, '')}>✕ No folder</button>
                    <div class="menu-sep"></div>
                    <button class="menu-item menu-item--danger" @click=${() => { this.confirmDelete = d.id; }}>🗑 Delete deck</button>`}
              </div>`
            : nothing}
        </div>
      </div>`;
  }
}

declare global {
  interface HTMLElementTagNameMap { 'library-page': LibraryPage }
}
